import React, { useState, createContext, useEffect } from 'react'
import Cookies from 'js-cookie'
import queryString from 'query-string'
import {
  useQuery,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { type DiscountCoupon } from '@mc/api'
import {
  DISCOUNT_COOKIE_KEY,
  DISCOUNT_MODAL_SHOULD_BE_SHOWN,
  DISCOUNT_VIEWED,
  ONE_WEEK,
} from '@mc/constants'
import { usePromotion, isPromoCode } from '@mc/promo-utils'
import { useHideCouponsIntroPricingExperiment } from '../../../experiments/prcng-338_v1_intro_pricing'
import { fetchDiscountData } from '../../../lib/helpers'
import { trackPromoModalCta, trackPromoModalViewed } from '../events'

interface ContextProviderProps {
  children: React.ReactNode
}

export interface DiscountModalContextValue {
  discount: DiscountCoupon | null | undefined
  isLoading: boolean
  showModal: boolean
  onClose: (action: string) => void
  query: queryString.ParsedQuery<string> | undefined
  pathname: string
}

export const queryClient = new QueryClient()

const DiscountModalContext = createContext<DiscountModalContextValue | null>(
  null,
)

const DataContextProvider = ({ children }: ContextProviderProps) => {
  const [showModal, setShowModal] = useState(false)
  const [query, setQuery] = useState<queryString.ParsedQuery<string>>()
  const [pathname, setPathname] = useState('')
  const [viewedDiscountSlug, setViewedDiscountSlug] = useState<string>()

  const { isPromotionOn } = usePromotion()

  useEffect(() => {
    setQuery(queryString.parse(window.location.search))
    setPathname(window.location.pathname)
  }, [])

  useEffect(() => {
    const currentSessionSlug = sessionStorage.getItem(DISCOUNT_VIEWED)

    if (viewedDiscountSlug && viewedDiscountSlug !== currentSessionSlug) {
      sessionStorage.setItem(DISCOUNT_VIEWED, viewedDiscountSlug)
      setViewedDiscountSlug(viewedDiscountSlug)
    } else {
      setViewedDiscountSlug(currentSessionSlug || '')
    }
  }, [viewedDiscountSlug])

  const discountQueryParam = query?.promo_id?.toString() || ''
  const discountCookie = Cookies.get(DISCOUNT_COOKIE_KEY) || ''
  const discountSlug = discountQueryParam || discountCookie
  const discountPreviouslyViewed = viewedDiscountSlug === discountSlug
  const noPromoCodeApplied = !isPromoCode(discountSlug)

  const shouldHideByExperiment =
    useHideCouponsIntroPricingExperiment(discountSlug)

  const shouldShowModal =
    !!discountSlug &&
    !discountPreviouslyViewed &&
    noPromoCodeApplied &&
    typeof viewedDiscountSlug !== 'undefined' &&
    !shouldHideByExperiment

  const { data: discount, isLoading } = useQuery({
    queryKey: ['discount', discountSlug],
    queryFn: () => fetchDiscountData(discountSlug),
    enabled: shouldShowModal,
  })

  if ((discount || isLoading) && !showModal && shouldShowModal)
    setShowModal(true)

  // If promo flag is on we don't want to overwrite the DISCOUNT_COOKIE_KEY
  // with the discountQueryParam
  if (!isPromotionOn) {
    if (discountQueryParam) {
      if (!discountCookie || discountCookie !== discountQueryParam)
        Cookies.set(DISCOUNT_COOKIE_KEY, discountQueryParam, {
          expires: ONE_WEEK,
        })
    }
  }

  useEffect(() => {
    if (discount) trackPromoModalViewed({ discount })
  }, [discount])

  useEffect(() => {
    /*
     * in order to avoid collision with other modals, it is set this key to
     * let know other modals about the chance of the modal being shown.
     */
    window.sessionStorage.setItem(
      DISCOUNT_MODAL_SHOULD_BE_SHOWN,
      ((discount || isLoading) && shouldShowModal).toString(),
    )
  }, [discount, isLoading, shouldShowModal])

  const onClose = (action: string) => {
    if (discount) trackPromoModalCta(action, discount)
    setViewedDiscountSlug(discountSlug)
    setShowModal(false)
  }

  return (
    <DiscountModalContext.Provider
      value={{
        discount,
        isLoading,
        showModal,
        onClose,
        query,
        pathname,
      }}
    >
      {children}
    </DiscountModalContext.Provider>
  )
}

export const DiscountModalContextProvider = ({
  children,
}: ContextProviderProps) => (
  <QueryClientProvider client={queryClient}>
    <DataContextProvider>{children}</DataContextProvider>
  </QueryClientProvider>
)

export const useDiscountModal = () => {
  const ctx = React.useContext(DiscountModalContext)
  if (!ctx) {
    throw new Error(
      'DiscountModalContext cannot be used outside of DiscountModalContextProvider',
    )
  }
  return ctx
}
